import { ServerTable } from '@civilgrid/ui/components';
import { buildSearchPayload, useTableStoreState } from '@civilgrid/ui/hooks';
import { createFilterGroup, createFilterLine } from '@civilgrid/ui/utils';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type ExportEscalation, listExportEscalations } from '~/api/internal';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { EXPORT_ESCALATION_POLL_INTERVAL_MS, EXPORT_ESCALATION_REASON_CONFIG } from './constants';
import { ExportEscalationDetailDialog } from './ExportEscalationDetailDialog';
import { getExportEscalationColumns } from './exportEscalationColumns';

// The server can honestly still report isRunning: false for a few seconds after dispatch — the job
// records triggeredAt itself once it starts (see ExportEscalationDetailDialog's runMutation). Unlike
// isRunning, a poll response can't overwrite this sentinel, mirroring ExportHistory's awaitingSubmissionId.
const JUST_TRIGGERED_TIMEOUT_MS = 60_000;

export default function EscalationsTable({ reason }: { reason: ExportEscalation['escalationReason'] }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [justTriggeredIds, setJustTriggeredIds] = useState<Set<string>>(new Set());

  const handleView = useCallback((escalation: ExportEscalation) => {
    capturePosthogEvent(POSTHOG_EVENTS.exportEscalationViewed, {
      escalation_id: escalation.id,
      project_id: escalation.projectId,
      escalation_reason: escalation.escalationReason,
    });
    setSelectedId(escalation.id);
  }, []);

  const handleRunTriggered = useCallback((id: string) => {
    setJustTriggeredIds((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setJustTriggeredIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, JUST_TRIGGERED_TIMEOUT_MS);
  }, []);

  const columns = useMemo(() => getExportEscalationColumns(handleView), [handleView]);

  const stateControl = useTableStoreState({
    defaultPageSize: 25,
    initialSorting: [{ id: 'requestedAt', desc: true }],
    initialFilters: createFilterGroup([createFilterLine('escalationStatus', 'neq', 'archived')]),
  });

  const { pagination, sorting, filters, globalFilter } = stateControl;

  const query = useQuery({
    gcTime: 0,
    refetchOnWindowFocus: false,
    // Poll while a row is running, or while we're still waiting on the server to confirm one we just
    // triggered — mirroring ExportHistory's project-level history panel.
    refetchInterval: (q) =>
      justTriggeredIds.size > 0 || q.state.data?.items.some((item) => item.isRunning)
        ? EXPORT_ESCALATION_POLL_INTERVAL_MS
        : false,
    queryKey: ['admin-export-escalations', reason, { pagination, sorting, filters, globalFilter }],
    queryFn: async () => {
      // The tab fixes the reason, not the user, so it's merged in here rather than into
      // stateControl's filters — never rendered as a removable chip.
      const searchPayload = buildSearchPayload({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting,
        filters: createFilterGroup([...filters.items, createFilterLine('escalationReason', 'eq', reason)]),
        globalFilter,
      });

      return listExportEscalations(searchPayload);
    },
  });

  // Drop a row from the sentinel set once the server confirms it either started or already resolved.
  useEffect(() => {
    if (justTriggeredIds.size === 0) return;
    const resolved = query.data?.items.filter(
      (item) => justTriggeredIds.has(item.id) && (item.isRunning || item.escalationStatus !== 'pending'),
    );
    if (!resolved?.length) return;

    setJustTriggeredIds((prev) => {
      const next = new Set(prev);
      for (const item of resolved) next.delete(item.id);
      return next;
    });
  }, [query.data, justTriggeredIds]);

  return (
    <>
      <ServerTable
        id="admin-export-escalations"
        aria-label={`${EXPORT_ESCALATION_REASON_CONFIG[reason].tabLabel} escalations`}
        columns={columns}
        data={{
          data: query.data?.items ?? [],
          rowCount: query.data?.count ?? 0,
          isLoading: query.isLoading,
          isFetching: query.isFetching,
          error: query.error || null,
        }}
        stateControl={stateControl}
        enableRowSelection={false}
        enableColumnGrouping={false}
        isLoading={query.isLoading}
        className="h-full"
      />
      <ExportEscalationDetailDialog
        escalationId={selectedId}
        reason={reason}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        onRunTriggered={handleRunTriggered}
      />
    </>
  );
}
